/**
 * Route guard that redirects to /login when there's no authenticated MSAL
 * account. UI-only — the backend re-checks every request (CLAUDE.md §5.3 —
 * no client-trusted authorisation).
 *
 * It waits while MSAL starts up (ADR-0053). With the cache in sessionStorage
 * a refreshed page sees the account before MSAL has finished initialising:
 * redirecting then would bounce it to /login, and rendering the page then
 * would ask for a token MSAL cannot yet give, so /me fails and the page
 * reads as "no role".
 *
 * The page that was asked for travels to `/login` as `state.from`, so
 * sign-in returns there (`returnTo.ts`, ADR-0070).
 */

import type { PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";

export function RequireAuth({ children }: PropsWithChildren) {
  const { accounts, inProgress } = useMsal();
  const { pathname, search, hash } = useLocation();
  if (inProgress === InteractionStatus.Startup) return null;
  if (accounts.length === 0) {
    return <Navigate to="/login" replace state={{ from: `${pathname}${search}${hash}` }} />;
  }
  return <>{children}</>;
}
